// One small app store, no state library.
//
// Holds the things every screen needs: locale, the signed in display name,
// the active trip, that trip's moments, the outbox backlog, and the sync
// status. Server data is mirrored into localStorage so the app still renders
// a real diary with no network, which is the offline first requirement.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { apiClient } from "../lib/api";
import { upgradeGeoProviderIfNative } from "../lib/geo";
import { listOutbox, onOutboxChange, type OutboxMoment } from "../lib/outbox";
import { drainOutbox, onSyncStatus, startSync, type SyncStatus } from "../lib/sync";
import {
  readStoredLocale,
  storeLocale,
  translate,
  type Locale,
  type TranslationKey,
} from "../lib/i18n";
import { isAuthoredMoment } from "../lib/types";
import type {
  AuthoredMoment,
  CreateTripInput,
  MeUser,
  Moment,
  Privacy,
  ProfilePatch,
  PublicUser,
  Trip,
  WishlistItem,
} from "../lib/types";

const KEY_ACTIVE_TRIP = "traveldiary.activeTrip";
const KEY_DISPLAY_NAME = "traveldiary.displayName";
const KEY_TRIPS = "traveldiary.trips";
const KEY_EXPLORE = "traveldiary.explore";
const KEY_WISHLIST = "traveldiary.wishlist";
const KEY_PRIVACY = "traveldiary.defaultPrivacy";
const keyMoments = (tripId: string) => `traveldiary.moments.${tripId}`;

function readLocal<T>(key: string, fallback: T): T {
  try {
    const raw = globalThis.localStorage?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeLocal(key: string, value: unknown): void {
  try {
    globalThis.localStorage?.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or disabled, the app still works for this session.
  }
}

/**
 * "unknown" is the boot state, "unavailable" means the API could not be
 * reached at all. Only "anonymous" (the API answered, nobody is signed in)
 * shows the login screen: a flaky network must never lock a traveller out of
 * their own cached diary.
 */
export type AuthState = "unknown" | "anonymous" | "signedIn" | "unavailable";

export interface AppContextValue {
  locale: Locale;
  setLocale(locale: Locale): void;
  t(key: TranslationKey): string;

  authState: AuthState;
  authUser: PublicUser | null;
  signIn(username: string, password: string): Promise<void>;
  signUp(input: { username: string; password: string; displayName: string }): Promise<void>;
  signOut(): Promise<void>;

  displayName: string;
  setDisplayName(name: string): void;

  /**
   * The signed in traveller's own record. `avatarSeed` on here is identity:
   * editing displayName must never change it, so `saveProfile` sends only
   * the fields the traveller actually edited.
   */
  me: MeUser | null;
  refreshProfile(): Promise<void>;
  saveProfile(patch: ProfilePatch): Promise<void>;
  uploadAvatarPhoto(file: Blob, filename: string): Promise<void>;

  /** Privacy a new diary entry starts with. Per device, set in Profil. */
  defaultPrivacy: Privacy;
  setDefaultPrivacy(privacy: Privacy): void;

  /** Saved destinations. Private to the owner, never on a public surface. */
  wishlist: WishlistItem[];
  wishlistFailed: boolean;
  refreshWishlist(): Promise<void>;
  saveToWishlist(item: WishlistItem): Promise<void>;
  removeFromWishlist(placeId: string): Promise<void>;

  trips: Trip[];
  tripsFailed: boolean;
  refreshTrips(): Promise<void>;
  createTrip(input: CreateTripInput): Promise<Trip>;

  activeTripId: string | null;
  setActiveTripId(tripId: string | null): void;
  activeTrip: Trip | null;

  moments: Moment[];
  refreshMoments(tripId: string): Promise<void>;
  /** Accepts a trip moment or a public one, and routes it to the right list. */
  upsertMoment(moment: Moment | AuthoredMoment): void;
  dropMoment(momentId: string): void;

  /** Optimistic like state, reconciled with the server's answer. */
  applyLike(momentId: string, state: { liked: boolean; likeCount: number }): void;
  bumpCommentCount(momentId: string, delta: number): void;

  /** Public and friends moments from every traveller, newest first. */
  exploreMoments: AuthoredMoment[];
  exploreFailed: boolean;
  refreshExplore(): Promise<void>;

  /**
   * A destination handed from the wishlist to the itinerary. This is the
   * "otomatis konek ke yang lain" wire: Wishlist sets it and navigates,
   * Itinerary reads it once to prefill its add form, then clears it.
   */
  itineraryDraft: ItineraryDraft | null;
  setItineraryDraft(draft: ItineraryDraft | null): void;

  /** Set by Explore to pull the map onto one coordinate. */
  mapFocus: { lat: number; lng: number; momentId: string | null; nonce: number } | null;
  focusOnMap(target: { lat: number; lng: number; momentId?: string | null }): void;

  pendingMoments: OutboxMoment[];
  sync: SyncStatus;
  syncNow(): void;
}

/** A destination on its way from the wishlist into a plan. */
export interface ItineraryDraft {
  placeId: string | null;
  placeName: string;
  lat: number | null;
  lng: number | null;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale());
  const [displayName, setDisplayNameState] = useState<string>(
    () => readLocal<string>(KEY_DISPLAY_NAME, "") || "Traveler",
  );
  const [trips, setTrips] = useState<Trip[]>(() => readLocal<Trip[]>(KEY_TRIPS, []));
  const [tripsFailed, setTripsFailed] = useState(false);
  const [activeTripId, setActiveTripIdState] = useState<string | null>(() =>
    readLocal<string | null>(KEY_ACTIVE_TRIP, null),
  );
  const [moments, setMoments] = useState<Moment[]>([]);
  const [pendingMoments, setPendingMoments] = useState<OutboxMoment[]>([]);
  const [sync, setSync] = useState<SyncStatus>({ state: "synced", pending: 0 });
  const [authState, setAuthState] = useState<AuthState>("unknown");
  const [authUser, setAuthUser] = useState<PublicUser | null>(null);
  const [exploreMoments, setExploreMoments] = useState<AuthoredMoment[]>(() =>
    readLocal<AuthoredMoment[]>(KEY_EXPLORE, []),
  );
  const [exploreFailed, setExploreFailed] = useState(false);
  const [mapFocus, setMapFocus] = useState<AppContextValue["mapFocus"]>(null);
  const [me, setMe] = useState<MeUser | null>(null);
  const [wishlist, setWishlist] = useState<WishlistItem[]>(() =>
    readLocal<WishlistItem[]>(KEY_WISHLIST, []),
  );
  const [wishlistFailed, setWishlistFailed] = useState(false);
  const [defaultPrivacy, setDefaultPrivacyState] = useState<Privacy>(() =>
    readLocal<Privacy>(KEY_PRIVACY, "private"),
  );
  const [itineraryDraft, setItineraryDraft] = useState<ItineraryDraft | null>(null);
  const loadedTripRef = useRef<string | null>(null);

  const t = useCallback((key: TranslationKey) => translate(locale, key), [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    storeLocale(next);
    if (globalThis.document) globalThis.document.documentElement.lang = next;
  }, []);

  const setDisplayName = useCallback((name: string) => {
    const trimmed = name.trim() || "Traveler";
    setDisplayNameState(trimmed);
    writeLocal(KEY_DISPLAY_NAME, trimmed);
  }, []);

  const setActiveTripId = useCallback((tripId: string | null) => {
    setActiveTripIdState(tripId);
    writeLocal(KEY_ACTIVE_TRIP, tripId);
  }, []);

  const setDefaultPrivacy = useCallback((privacy: Privacy) => {
    setDefaultPrivacyState(privacy);
    writeLocal(KEY_PRIVACY, privacy);
  }, []);

  const refreshWishlist = useCallback(async () => {
    try {
      const fresh = await apiClient.listWishlist();
      if (fresh === null) {
        // This API build has no /wishlist yet. The cached list stays, and
        // nothing is reported as broken for a route the server does not own.
        setWishlistFailed(false);
        return;
      }
      setWishlist(fresh);
      setWishlistFailed(false);
      writeLocal(KEY_WISHLIST, fresh);
    } catch {
      setWishlistFailed(true);
    }
  }, []);

  const saveToWishlist = useCallback(async (item: WishlistItem) => {
    // Optimistic: the swipe already animated, so the list must agree at once.
    setWishlist((current) => {
      const next = [item, ...current.filter((entry) => entry.placeId !== item.placeId)];
      writeLocal(KEY_WISHLIST, next);
      return next;
    });
    try {
      await apiClient.addToWishlist(item.placeId);
    } catch {
      // Kept locally. The next refresh reconciles with the server.
    }
  }, []);

  const removeFromWishlist = useCallback(async (placeId: string) => {
    setWishlist((current) => {
      const next = current.filter((entry) => entry.placeId !== placeId);
      writeLocal(KEY_WISHLIST, next);
      return next;
    });
    try {
      await apiClient.removeFromWishlist(placeId);
    } catch {
      // Same as above: local state leads, the server catches up.
    }
  }, []);

  const refreshTrips = useCallback(async () => {
    try {
      const fresh = await apiClient.listTrips();
      setTrips(fresh);
      setTripsFailed(false);
      writeLocal(KEY_TRIPS, fresh);
    } catch {
      setTripsFailed(true);
    }
  }, []);

  const refreshExplore = useCallback(async () => {
    try {
      const fresh = await apiClient.listExploreMoments({ limit: 50 });
      setExploreMoments(fresh);
      setExploreFailed(false);
      writeLocal(KEY_EXPLORE, fresh);
    } catch {
      setExploreFailed(true);
    }
  }, []);

  const focusOnMap = useCallback(
    (target: { lat: number; lng: number; momentId?: string | null }) => {
      setMapFocus((current) => ({
        lat: target.lat,
        lng: target.lng,
        momentId: target.momentId ?? null,
        nonce: (current?.nonce ?? 0) + 1,
      }));
    },
    [],
  );

  const adoptUser = useCallback((user: PublicUser) => {
    setAuthUser(user);
    setAuthState("signedIn");
    setDisplayNameState(user.displayName);
    writeLocal(KEY_DISPLAY_NAME, user.displayName);
    // The session user is enough to render Profil; /users/me adds the bio.
    setMe((current) => ({ ...(current ?? {}), ...user }));
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const profile = await apiClient.profile();
      if (profile) setMe(profile);
    } catch {
      // Falls back to the session user already held in `me`.
    }
  }, []);

  /**
   * Sends ONLY what was edited. This is the rule that keeps a traveller's
   * face stable: `avatarSeed` is absent from the patch unless the traveller
   * deliberately regenerated it, so fixing a typo in a name can never
   * redraw the character.
   */
  const saveProfile = useCallback(async (patch: ProfilePatch) => {
    const updated = await apiClient.updateMe(patch);
    setMe(updated);
    setAuthUser(updated);
    setDisplayNameState(updated.displayName);
    writeLocal(KEY_DISPLAY_NAME, updated.displayName);
  }, []);

  const uploadAvatarPhoto = useCallback(async (file: Blob, filename: string) => {
    const { photoUrl, profile } = await apiClient.uploadAvatar(file, filename);
    // The upload answers with the refreshed profile, so the header and every
    // avatar update from this one call. `photoUrl` is the fallback for an
    // older API build that only returns the URL.
    if (profile) {
      setMe(profile);
      setAuthUser(profile);
      return;
    }
    setMe((current) => (current ? { ...current, photoUrl } : current));
    setAuthUser((current) => (current ? { ...current, photoUrl } : current));
  }, []);

  const signIn = useCallback(
    async (username: string, password: string) => {
      const { user } = await apiClient.login(username.trim(), password);
      adoptUser(user);
    },
    [adoptUser],
  );

  const signUp = useCallback(
    async (input: { username: string; password: string; displayName: string }) => {
      const { user } = await apiClient.register({
        username: input.username.trim(),
        password: input.password,
        displayName: input.displayName.trim(),
      });
      adoptUser(user);
    },
    [adoptUser],
  );

  const signOut = useCallback(async () => {
    try {
      await apiClient.clearPresence();
    } catch {
      // Presence expires on its own, a failure here must not block sign out.
    }
    try {
      await apiClient.logout();
    } catch {
      // Already gone as far as this device is concerned.
    }
    setAuthUser(null);
    setMe(null);
    setAuthState("anonymous");
    setWishlist([]);
    writeLocal(KEY_WISHLIST, []);
    setTrips([]);
    setMoments([]);
    setExploreMoments([]);
    setActiveTripIdState(null);
    writeLocal(KEY_ACTIVE_TRIP, null);
    writeLocal(KEY_TRIPS, []);
    writeLocal(KEY_EXPLORE, []);
  }, []);

  const createTrip = useCallback(
    async (input: CreateTripInput) => {
      const trip = await apiClient.createTrip(input);
      setTrips((current) => {
        const next = [trip, ...current.filter((item) => item.id !== trip.id)];
        writeLocal(KEY_TRIPS, next);
        return next;
      });
      setTripsFailed(false);
      return trip;
    },
    [],
  );

  const refreshMoments = useCallback(async (tripId: string) => {
    const cached = readLocal<Moment[]>(keyMoments(tripId), []);
    setMoments(cached);
    try {
      const fresh = await apiClient.listMoments(tripId);
      setMoments(fresh);
      writeLocal(keyMoments(tripId), fresh);
    } catch {
      // Offline, the cached list above stays on screen.
    }
  }, []);

  const upsertMoment = useCallback((moment: Moment | AuthoredMoment) => {
    // A public moment carries its author, so it belongs to the explore feed,
    // which is what the map's destination pins are grouped from. A bare trip
    // moment belongs to this traveller's own trip list. Some moments are both.
    if (isAuthoredMoment(moment)) {
      setExploreMoments((current) => {
        const next = [moment, ...current.filter((item) => item.id !== moment.id)].sort((a, b) =>
          b.capturedAt.localeCompare(a.capturedAt),
        );
        writeLocal(KEY_EXPLORE, next);
        return next;
      });
    }
    setMoments((current) => {
      if (!current.some((item) => item.tripId === moment.tripId) && current.length > 0) {
        return current;
      }
      const next = [moment, ...current.filter((item) => item.id !== moment.id)].sort((a, b) =>
        b.capturedAt.localeCompare(a.capturedAt),
      );
      writeLocal(keyMoments(moment.tripId), next);
      return next;
    });
  }, []);

  const applyLike = useCallback((momentId: string, state: { liked: boolean; likeCount: number }) => {
    setExploreMoments((current) => {
      const next = current.map((item) =>
        item.id === momentId
          ? { ...item, likedByMe: state.liked, likeCount: state.likeCount }
          : item,
      );
      writeLocal(KEY_EXPLORE, next);
      return next;
    });
  }, []);

  const bumpCommentCount = useCallback((momentId: string, delta: number) => {
    setExploreMoments((current) => {
      const next = current.map((item) =>
        item.id === momentId
          ? { ...item, commentCount: Math.max(0, (item.commentCount ?? 0) + delta) }
          : item,
      );
      writeLocal(KEY_EXPLORE, next);
      return next;
    });
  }, []);

  const dropMoment = useCallback((momentId: string) => {
    setMoments((current) => {
      const next = current.filter((item) => item.id !== momentId);
      const tripId = current.find((item) => item.id === momentId)?.tripId;
      if (tripId) writeLocal(keyMoments(tripId), next);
      return next;
    });
  }, []);

  const syncNow = useCallback(() => {
    void drainOutbox();
  }, []);

  useEffect(() => {
    if (globalThis.document) globalThis.document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    const stop = startSync();
    const unsubscribeStatus = onSyncStatus(setSync);
    const unsubscribeOutbox = onOutboxChange(() => {
      void listOutbox().then((items) => {
        setPendingMoments(items.filter((item): item is OutboxMoment => item.kind === "moment"));
      });
    });
    // Native geolocation is an optional upgrade, the browser provider is the
    // default and stays in place if this resolves to nothing.
    void upgradeGeoProviderIfNative();
    // Sequenced on purpose, not three bare `void` calls: the reads below are
    // authenticated, so who is signed in has to be settled first. Otherwise
    // GET /trips races GET /auth/me, answers 401, and the Trips screen shows
    // "failed to load" on a perfectly healthy API.
    void (async () => {
      let signedIn = false;
      try {
        const current = await apiClient.me();
        if (current.user) {
          signedIn = true;
          setAuthUser(current.user);
          setMe((existing) => ({ ...(existing ?? {}), ...current.user! }));
          setAuthState("signedIn");
          setDisplayNameState(current.user.displayName);
        } else {
          setAuthState("anonymous");
        }
      } catch {
        // The API is unreachable. Stay out of the way and let the cached
        // diary render, this is an offline first app.
        setAuthState("unavailable");
      }
      // The signed in effect below owns the data load, so nothing is fetched
      // twice here.
      void signedIn;
    })();
    return () => {
      unsubscribeStatus();
      unsubscribeOutbox();
      stop();
    };
    // Runs once: the drain loop and subscriptions live for the app's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Signing in mid session has to load the same data the boot sequence loads,
  // otherwise the map and the trip list stay empty until you happen to open
  // the screen that fetches them.
  useEffect(() => {
    if (authState !== "signedIn") return;
    void (async () => {
      await refreshTrips();
      await refreshExplore();
      await refreshProfile();
      await refreshWishlist();
    })();
  }, [authState, refreshTrips, refreshExplore, refreshProfile, refreshWishlist]);

  /**
   * A traveller who has trips but has never picked one would otherwise see an
   * empty Beranda, an empty Itinerary and a Capture screen that refuses to
   * open, all while their trips sit one tap away. When exactly nothing is
   * chosen, the most recently created trip is adopted. An explicit choice is
   * never overridden, this only fills a null.
   */
  useEffect(() => {
    if (activeTripId || trips.length === 0) return;
    const newest = [...trips].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
    if (newest) setActiveTripId(newest.id);
  }, [activeTripId, trips, setActiveTripId]);

  useEffect(() => {
    if (!activeTripId || loadedTripRef.current === activeTripId) return;
    loadedTripRef.current = activeTripId;
    void refreshMoments(activeTripId);
  }, [activeTripId, refreshMoments]);

  const activeTrip = useMemo(
    () => trips.find((trip) => trip.id === activeTripId) ?? null,
    [trips, activeTripId],
  );

  const value = useMemo<AppContextValue>(
    () => ({
      locale,
      setLocale,
      t,
      authState,
      authUser,
      signIn,
      signUp,
      signOut,
      exploreMoments,
      exploreFailed,
      refreshExplore,
      mapFocus,
      focusOnMap,
      displayName,
      setDisplayName,
      me,
      refreshProfile,
      saveProfile,
      uploadAvatarPhoto,
      defaultPrivacy,
      setDefaultPrivacy,
      itineraryDraft,
      setItineraryDraft,
      wishlist,
      wishlistFailed,
      refreshWishlist,
      saveToWishlist,
      removeFromWishlist,
      trips,
      tripsFailed,
      refreshTrips,
      createTrip,
      activeTripId,
      setActiveTripId,
      activeTrip,
      moments,
      refreshMoments,
      upsertMoment,
      dropMoment,
      applyLike,
      bumpCommentCount,
      pendingMoments,
      sync,
      syncNow,
    }),
    [
      locale,
      setLocale,
      t,
      authState,
      authUser,
      signIn,
      signUp,
      signOut,
      exploreMoments,
      exploreFailed,
      refreshExplore,
      mapFocus,
      focusOnMap,
      displayName,
      setDisplayName,
      me,
      refreshProfile,
      saveProfile,
      uploadAvatarPhoto,
      defaultPrivacy,
      setDefaultPrivacy,
      itineraryDraft,
      setItineraryDraft,
      wishlist,
      wishlistFailed,
      refreshWishlist,
      saveToWishlist,
      removeFromWishlist,
      trips,
      tripsFailed,
      refreshTrips,
      createTrip,
      activeTripId,
      setActiveTripId,
      activeTrip,
      moments,
      refreshMoments,
      upsertMoment,
      dropMoment,
      applyLike,
      bumpCommentCount,
      pendingMoments,
      sync,
      syncNow,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const value = useContext(AppContext);
  if (!value) throw new Error("useApp must be used inside <AppProvider>");
  return value;
}
